// Dotgrad — the Studio-mode visual engine. Renders a multi-stop gradient as
// a grid of colored cells with thin background gaps (the halftone-dot look).
// Optional OKLab interpolation gives perceptually-rich rainbow ramps.

import type { LayerSpec } from '@/core/pipeline';
import { parseHex, lerpRGB, type RGB } from '@/lib/color';
import { lerpRGBOklab } from '@/lib/oklab';
import { mulberry32 } from '@/lib/rng';
import { SWATCHES, type Stop } from '@/lib/swatches';
import { applyMotion, MOTION_PRESETS } from '@/lib/motionPresets';
import { SHAPES, SHAPE_IDS } from './shapes';

type Shape = string;

type DotgradParams = {
  shape: Shape;
  angle: number;     // 0..360
  spread: number;    // 8..2048 (pixels per gradient cycle)
  offset: number;    // -100..100 (% of cycle)
  repeat: number;    // 1..8 (extra cycles per traversal)
  centerX: number;   // -100..100 (% of canvas)
  centerY: number;   // -100..100 (% of canvas)
  cell: number;      // 2..16 (dot cell size in px)
  gap: number;       // 0..4 (background pixels around each dot)
  quant: number;     // 0..16 (color quantization steps; 0 = continuous)
  noise: number;     // 0..100 (% jitter)
  mirror: boolean;   // mirror gradient at midpoint
  oklab: boolean;    // OKLab interpolation
  stops: Stop[];     // gradient stops, t in 0..1
  bg: string;        // gap colour
  motion: string;    // motion preset id ('none' or one of MOTION_PRESETS)
  motionSpeed: number;    // 10..400 (% of base speed)
  motionStrength: number; // 0..200 (% of base amplitude)
};

// First swatch becomes the default ramp.
const DEFAULT_STOPS: Stop[] = (SWATCHES[0]?.stops ?? [
  { t: 0, color: '#000000' },
  { t: 1, color: '#ffffff' },
]) as Stop[];

const DEFAULTS: DotgradParams = {
  shape: 'linear',
  angle: 25,
  spread: 800,
  offset: 0,
  repeat: 1,
  centerX: 0,
  centerY: 0,
  cell: 8,
  gap: 1,
  quant: 0,
  noise: 0,
  mirror: false,
  oklab: true,
  stops: DEFAULT_STOPS,
  bg: '#000000',
  motion: 'none',
  motionSpeed: 100,
  motionStrength: 100,
};

export const dotgradSpec: LayerSpec = {
  kind: 'dotgrad' as never, // registered as a real LayerKind via type extension
  category: 'generator',
  animated: true,
  label: 'Dotgrad',
  defaults: { ...DEFAULTS },
  schema: {
    shape:    { type: 'select', label: 'Shape', options: SHAPE_IDS, default: 'linear' },
    angle:    { type: 'number', label: 'Angle',    min: 0,   max: 360,  step: 1,   default: 25 },
    spread:   { type: 'number', label: 'Spread',   min: 8,   max: 2048, step: 1,   default: 800 },
    offset:   { type: 'number', label: 'Offset',   min: -100, max: 100, step: 1,   default: 0 },
    repeat:   { type: 'int',    label: 'Repeat',   min: 1,   max: 8,   step: 1,   default: 1 },
    centerX:  { type: 'number', label: 'Center X', min: -100, max: 100, step: 1,   default: 0 },
    centerY:  { type: 'number', label: 'Center Y', min: -100, max: 100, step: 1,   default: 0 },
    cell:     { type: 'int',    label: 'Cell',     min: 2,   max: 64,   step: 1,   default: 8 },
    gap:      { type: 'int',    label: 'Gap',      min: 0,   max: 16,   step: 1,   default: 1 },
    quant:    { type: 'int',    label: 'Quant',    min: 0,   max: 16,   step: 1,   default: 0 },
    noise:    { type: 'number', label: 'Noise',    min: 0,   max: 100,  step: 1,   default: 0 },
    mirror:   { type: 'bool',   label: 'Mirror',   default: false },
    oklab:    { type: 'bool',   label: 'OKLab',    default: true },
    bg:       { type: 'color',  label: 'BG',       default: '#000000' },
    motion:         { type: 'select', label: 'Motion', options: ['none', ...MOTION_PRESETS.map((m) => m.id)], default: 'none' },
    motionSpeed:    { type: 'number', label: 'Motion Speed',    min: 10, max: 400, step: 1, default: 100 },
    motionStrength: { type: 'number', label: 'Motion Strength', min: 0,  max: 200, step: 1, default: 100 },
  },
  run: (_input, params, ctx) => {
    const base = { ...DEFAULTS, ...(params as Partial<DotgradParams>) };
    const motionId = base.motion ?? 'none';
    const overrides = motionId !== 'none'
      ? applyMotion(base, motionId, ctx.time, (base.motionSpeed ?? 100) / 100, (base.motionStrength ?? 100) / 100)
      : {};
    const p = { ...base, ...overrides };
    const stops = normalizeStops(Array.isArray(p.stops) ? p.stops : DEFAULT_STOPS);
    const bg = parseHex(p.bg) ?? ([0, 0, 0] as RGB);
    const w = ctx.width, h = ctx.height;
    const out = new ImageData(w, h);
    const data = out.data;

    // Cap is 256 (was 32) so high-resolution exports can scale cell without
    // being silently clipped down to a microscopic dot grid.
    const cell = clampInt(p.cell, 2, 256);
    const gap  = Math.min(Math.floor(cell / 2 - 0.5), Math.max(0, Math.floor(p.gap)));
    const cellsW = Math.ceil(w / cell);
    const cellsH = Math.ceil(h / cell);

    const cx = (w - 1) / 2 + ((p.centerX / 100) * w) / 2;
    const cy = (h - 1) / 2 + ((p.centerY / 100) * h) / 2;
    const angle = (p.angle * Math.PI) / 180;
    const dx = Math.cos(angle), dy = Math.sin(angle);
    const spread = Math.max(2, p.spread);
    const offset = p.offset / 100;
    const repeat = Math.max(1, Math.floor(p.repeat));
    const mirror = !!p.mirror;
    const oklab  = !!p.oklab;
    const quant  = Math.max(0, Math.min(16, Math.floor(p.quant)));
    const noise  = Math.max(0, Math.min(100, p.noise)) / 100;
    const rng    = mulberry32(ctx.seed);
    const maxR   = Math.max(1, Math.hypot(Math.max(cx, w - cx), Math.max(cy, h - cy)));

    // Pre-compute one colour per cell.
    const cellRGB = new Uint8ClampedArray(cellsW * cellsH * 3);
    for (let cyI = 0; cyI < cellsH; cyI++) {
      for (let cxI = 0; cxI < cellsW; cxI++) {
        const px = cxI * cell + cell / 2;
        const py = cyI * cell + cell / 2;
        const t  = computeT(p.shape, px, py, cx, cy, dx, dy, spread, maxR, offset);
        const tWrapped = wrap(t * repeat + offset, mirror);
        let rgb = sampleStops(stops, tWrapped, oklab);
        if (quant > 0) rgb = quantizeRGB(rgb, quant);
        if (noise > 0) {
          const n = (rng() - 0.5) * 255 * noise;
          rgb = [c255(rgb[0] + n), c255(rgb[1] + n), c255(rgb[2] + n)];
        }
        const idx = (cyI * cellsW + cxI) * 3;
        cellRGB[idx]     = rgb[0];
        cellRGB[idx + 1] = rgb[1];
        cellRGB[idx + 2] = rgb[2];
      }
    }

    // Paint pixels: dot interior gets cell colour, gap pixels get bg.
    const dotStart = gap;
    const dotEnd = cell - gap;
    let i = 0;
    for (let py = 0; py < h; py++) {
      const cyI = Math.floor(py / cell);
      const inCellY = py - cyI * cell;
      const yInDot = inCellY >= dotStart && inCellY < dotEnd;
      for (let px = 0; px < w; px++, i += 4) {
        const cxI = Math.floor(px / cell);
        const inCellX = px - cxI * cell;
        const xInDot = inCellX >= dotStart && inCellX < dotEnd;
        if (xInDot && yInDot) {
          const ci = (cyI * cellsW + cxI) * 3;
          data[i]     = cellRGB[ci]     as number;
          data[i + 1] = cellRGB[ci + 1] as number;
          data[i + 2] = cellRGB[ci + 2] as number;
        } else {
          data[i]     = bg[0];
          data[i + 1] = bg[1];
          data[i + 2] = bg[2];
        }
        data[i + 3] = 255;
      }
    }
    return out;
  },
};

// ---- Helpers --------------------------------------------------------------

function computeT(
  shape: Shape, px: number, py: number,
  cx: number, cy: number,
  dx: number, dy: number,
  spread: number, maxR: number,
  _offset: number,
): number {
  const fn = SHAPES[shape] ?? SHAPES['linear']!;
  return fn(px, py, cx, cy, dx, dy, spread, maxR);
}

function wrap(t: number, mirror: boolean): number {
  if (mirror) {
    const m = ((t % 2) + 2) % 2;
    return m > 1 ? 2 - m : m;
  }
  return ((t % 1) + 1) % 1;
}

type ParsedStop = { t: number; rgb: RGB };

function normalizeStops(stops: Stop[]): ParsedStop[] {
  const arr: ParsedStop[] = [];
  for (const s of stops) {
    const rgb = parseHex(s.color);
    if (rgb !== null) arr.push({ t: clamp01(s.t), rgb });
  }
  if (arr.length === 0) return [{ t: 0, rgb: [0, 0, 0] }, { t: 1, rgb: [255, 255, 255] }];
  arr.sort((a, b) => a.t - b.t);
  return arr;
}

function sampleStops(stops: ParsedStop[], t: number, oklab: boolean): RGB {
  if (stops.length === 1) return stops[0]!.rgb;
  if (t <= stops[0]!.t) return stops[0]!.rgb;
  if (t >= stops[stops.length - 1]!.t) return stops[stops.length - 1]!.rgb;
  for (let i = 0; i < stops.length - 1; i++) {
    const a = stops[i]!, b = stops[i + 1]!;
    if (t >= a.t && t <= b.t) {
      const span = b.t - a.t || 1;
      const u = (t - a.t) / span;
      return oklab ? lerpRGBOklab(a.rgb, b.rgb, u) : roundRGB(lerpRGB(a.rgb, b.rgb, u));
    }
  }
  return stops[stops.length - 1]!.rgb;
}

function quantizeRGB(rgb: RGB, levels: number): RGB {
  const L = levels;
  return [
    Math.round((Math.round((rgb[0] / 255) * (L - 1)) * 255) / (L - 1)),
    Math.round((Math.round((rgb[1] / 255) * (L - 1)) * 255) / (L - 1)),
    Math.round((Math.round((rgb[2] / 255) * (L - 1)) * 255) / (L - 1)),
  ];
}

function roundRGB(rgb: RGB): RGB {
  return [Math.round(rgb[0]), Math.round(rgb[1]), Math.round(rgb[2])];
}

function clampInt(n: number, lo: number, hi: number): number {
  const v = Math.floor(n);
  return v < lo ? lo : v > hi ? hi : v;
}
function clamp01(n: number): number { return n < 0 ? 0 : n > 1 ? 1 : n; }
function c255(n: number): number { return n < 0 ? 0 : n > 255 ? 255 : Math.round(n); }
